// Filename: PricingEngine.js
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '../../shared/navbar';
import { API_BASE_URL } from '../../config';
import './PricingEngine.css';

const PricingEngine = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const propertyIdFromUrl = searchParams.get('propertyId');
    const unitTypeIdFromUrl = searchParams.get('unitTypeId');
    
    // Property selection
    const [properties, setProperties] = useState([]);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [selectedUnitType, setSelectedUnitType] = useState(null);
    
    // Location input
    const [targetLocation, setTargetLocation] = useState('');
    const [maxListings, setMaxListings] = useState(15);
    
    // State management
    const [loading, setLoading] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [analysis, setAnalysis] = useState(null);
    const [error, setError] = useState(null);
    
    // Comparables data
    const [allComparables, setAllComparables] = useState([]);
    const [filteredComparables, setFilteredComparables] = useState([]);
    const [availableLocations, setAvailableLocations] = useState([]);
    
    // AI Model
    const [useHedonicModel, setUseHedonicModel] = useState(false);
    const [modelInfo, setModelInfo] = useState(null);
    const [trainingModel, setTrainingModel] = useState(false);
    const [trainingResults, setTrainingResults] = useState(null);
    
    // Pricing adjustments
    const [floorLevel, setFloorLevel] = useState(1);
    const [marketAdjustment, setMarketAdjustment] = useState(0);
    const [amenities, setAmenities] = useState({
        parking: false,
        storage: false,
        petFriendly: false,
        balcony: false
    });

    // ==================== DATA FETCHING ====================
    
    const fetchProperties = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/pricing-engine/properties`);
            if (response.ok) {
                const data = await response.json();
                setProperties(data);
            }
        } catch (err) {
            console.error('Error fetching properties:', err);
        }
    };

    const fetchComparablesFromDB = async (locationFilter = '', bedsFilter = null) => {
        try {
            const params = new URLSearchParams();
            if (locationFilter) params.set('location', locationFilter);
            if (bedsFilter != null && bedsFilter > 0) params.set('beds', String(bedsFilter));
            const url = `${API_BASE_URL}/api/pricing-engine/comparables${params.toString() ? '?' + params.toString() : ''}`;
            const response = await fetch(url);
            if (response.ok) {
                const data = await response.json();
                const comparables = data.comparable_properties || [];
                
                if (!locationFilter) {
                    setAllComparables(comparables);
                    // Extract unique locations (city/township)
                    const locations = [...new Set(comparables.map(p => p.township || extractCity(p.address)).filter(Boolean))];
                    setAvailableLocations(locations.sort());
                }
                
                return comparables;
            }
            return [];
        } catch (err) {
            console.error('Error fetching comparables:', err);
            return [];
        }
    };

    const fetchModelInfo = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/pricing-engine/model-info`);
            if (response.ok) {
                const data = await response.json();
                setModelInfo(data);
                setUseHedonicModel(data.model_available || false);
            }
        } catch (err) {
            console.error('Error fetching model info:', err);
        }
    };

    // ==================== HELPER FUNCTIONS ====================

    const extractCity = (address) => {
        if (!address) return '';
        const parts = address.split(',');
        if (parts.length >= 2) {
            return parts[parts.length - 2].trim();
        }
        return '';
    };

    const extractState = (address) => {
        if (!address) return '';
        const parts = address.split(',');
        if (parts.length >= 1) {
            const lastPart = parts[parts.length - 1].trim();
            const statePart = lastPart.split(' ')[0];
            return statePart;
        }
        return '';
    };

    // Filter comparables by location AND unit type (bedroom count)
    useEffect(() => {
        let filtered = allComparables;
        
        // Filter by location
        if (targetLocation) {
            filtered = filtered.filter(p => {
                const city = p.township || extractCity(p.address);
                return city?.toLowerCase().includes(targetLocation.toLowerCase());
            });
        }
        
        // Filter by bedroom count if unit type is selected
        if (selectedUnitType) {
            const targetBeds = selectedUnitType.bedroom_count || selectedUnitType.bedrooms;
            if (targetBeds) {
                filtered = filtered.map(p => {
                    // Filter units within each property to match bedroom count
                    if (p.units?.length > 0) {
                        const matchingUnits = p.units.filter(u => {
                            const unitBeds = parseInt(String(u.beds || '0').match(/\d+/)?.[0]) || 0;
                            // Allow ±1 bedroom variance for better comparables
                            return Math.abs(unitBeds - targetBeds) <= 1;
                        });
                        if (matchingUnits.length > 0) {
                            return { ...p, units: matchingUnits, _matchedBeds: true };
                        }
                    }
                    return null;
                }).filter(Boolean);
            }
        }
        
        setFilteredComparables(filtered);
    }, [targetLocation, allComparables, selectedUnitType]);

    // ==================== MAIN ACTION: ANALYZE MARKET ====================

    const analyzeMarket = async () => {
        if (!targetLocation.trim()) {
            setError('Please enter a city/location to analyze');
            return;
        }

        setLoading(true);
        setError(null);
        setStatusMessage('Checking database for existing data...');

        try {
            const bedsForFilter = selectedUnitType ? (selectedUnitType.bedroom_count || selectedUnitType.bedrooms) : null;
            // Step 1: Check if we have existing data for this location (optionally filtered by unit type)
            const existingData = await fetchComparablesFromDB(targetLocation, bedsForFilter);
            const existingCount = existingData.length;
            
            let combinedData = [...existingData];
            let scraped = 0;
            
            // Step 2: If no existing data, scrape using Scrapfly API
            if (existingCount === 0) {
                setStatusMessage(`No existing data for "${targetLocation}". Scraping via Scrapfly...`);
                
                // Format city for API
                const cityFormatted = targetLocation.toLowerCase().replace(/,?\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                
                try {
                    const scrapeResponse = await fetch(`${API_BASE_URL}/api/pricing-engine/scrape-city`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            city_input: cityFormatted,
                            max_listings: maxListings,
                            use_hedonic_model: useHedonicModel
                        })
                    });

                    if (!scrapeResponse.ok) {
                        const errorText = await scrapeResponse.text();
                        console.error('Scrape response error:', scrapeResponse.status, errorText);
                        throw new Error(`Server error: ${scrapeResponse.status}`);
                    }

                    const scrapeData = await scrapeResponse.json();
                    console.log('Scrape response:', scrapeData);
                    
                    if (scrapeData.properties_scraped > 0) {
                        scraped = scrapeData.properties_scraped;
                        setStatusMessage(`✅ Scraped ${scraped} properties! Loading...`);
                        
                        // Refresh from database to get the newly scraped data (all locations for dropdown)
                        await fetchComparablesFromDB();
                        combinedData = await fetchComparablesFromDB(targetLocation, bedsForFilter);
                    } else if (scrapeData.message) {
                        setStatusMessage(`ℹ️ ${scrapeData.message}`);
                    } else if (scrapeData.error) {
                        setError(`Scraping error: ${scrapeData.error}`);
                        setLoading(false);
                        return;
                    }
                } catch (fetchError) {
                    console.error('Scraping request failed:', fetchError);
                    setError(`Scraping failed: ${fetchError.message}. Please try again.`);
                    setLoading(false);
                    return;
                }
            } else {
                setStatusMessage(`Found ${existingCount} existing properties in database.`);
            }

            // Step 3: Calculate market statistics from combined data
            if (combinedData.length === 0) {
                setError('No properties found for this location. Try a different city.');
                setLoading(false);
                return;
            }

            setFilteredComparables(combinedData);

            // Step 4: Run analysis on the backend
            setStatusMessage('Analyzing market data...');
            
            const analysisResponse = await fetch(`${API_BASE_URL}/api/pricing-engine/analyze`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    location_filter: targetLocation,
                    use_hedonic_model: useHedonicModel,
                    // Include unit type specs for better analysis
                    unit_type: selectedUnitType ? {
                        bedrooms: selectedUnitType.bedroom_count || selectedUnitType.bedrooms,
                        bathrooms: selectedUnitType.bathroom_count || selectedUnitType.bathrooms,
                        square_feet: selectedUnitType.square_footage,
                        current_rent: selectedUnitType.minimum_expected_rent
                    } : null
                })
            });

            const analysisData = await analysisResponse.json();
            
            if (analysisResponse.ok) {
                setAnalysis(analysisData);
                const unitLabel = selectedUnitType 
                    ? ` (${selectedUnitType.bedroom_count || selectedUnitType.bedrooms}BR${selectedUnitType.bathroom_count || selectedUnitType.bathrooms ? '/' + (selectedUnitType.bathroom_count || selectedUnitType.bathrooms) + 'BA' : ''} matching)`
                    : '';
                const msg = existingCount > 0 
                    ? `✅ Analysis complete! Using ${combinedData.length} properties from database${unitLabel}.`
                    : `✅ Analysis complete! Scraped and analyzed ${scraped} new properties${unitLabel}.`;
                setStatusMessage(msg);
            } else {
                const errMsg = analysisData.error || 'Analysis failed';
                const noMatch = analysisData.no_unit_type_match;
                setError(noMatch 
                    ? `${errMsg} Try "Scrape More Data" for this location, or run analysis without selecting a unit type to see market-wide stats.`
                    : errMsg
                );
            }

        } catch (err) {
            setError('Error during analysis: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Scrape additional data for existing location
    const scrapeMore = async () => {
        if (!targetLocation.trim()) {
            setError('Please enter a location first');
            return;
        }

        setLoading(true);
        setError(null);
        setStatusMessage('Scraping additional properties...');

        try {
            const cityFormatted = targetLocation.toLowerCase().replace(/,?\s+/g, '-').replace(/[^a-z0-9-]/g, '');
            
            const scrapeResponse = await fetch(`${API_BASE_URL}/api/pricing-engine/scrape-city`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    city_input: cityFormatted,
                    max_listings: maxListings,
                    use_hedonic_model: useHedonicModel
                })
            });

            const scrapeData = await scrapeResponse.json();
            
            if (scrapeResponse.ok && scrapeData.properties_scraped > 0) {
                setStatusMessage(`✅ Added ${scrapeData.properties_scraped} new properties!`);
                
                const bedsForFilter = selectedUnitType ? (selectedUnitType.bedroom_count || selectedUnitType.bedrooms) : null;
                await fetchComparablesFromDB();
                const updated = await fetchComparablesFromDB(targetLocation, bedsForFilter);
                setFilteredComparables(updated);
                
                // Re-run analysis
                await analyzeMarket();
            } else {
                setStatusMessage('No new properties found to add.');
            }
        } catch (err) {
            setError('Error scraping: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const trainModel = async () => {
        setTrainingModel(true);
        setTrainingResults(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/pricing-engine/train-model`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    epochs: 50,
                    hidden_layers: [64, 32],
                    dropout_rate: 0.3
                })
            });

            if (response.ok) {
                const data = await response.json();
                setTrainingResults(data.results);
                fetchModelInfo();
            } else {
                const errorData = await response.json();
                setError('Model training failed: ' + errorData.error);
            }
        } catch (err) {
            setError('Error training model: ' + err.message);
        } finally {
            setTrainingModel(false);
        }
    };

    // ==================== COMPUTED VALUES ====================

    const getMarketStats = () => {
        // Use analysis data if available (includes Zillow fallback data)
        if (analysis && analysis.market_clearing_price) {
            return {
                avgRent: Math.round(analysis.market_clearing_price),
                avgPsf: parseFloat(analysis.price_per_sqft || 0).toFixed(2),
                totalUnits: analysis.comparable_count || 0,
                count: analysis.comparable_count || filteredComparables.length,
                avgSqft: analysis.avg_sqft || 0,
                zillowFallback: analysis.zillow_fallback || false
            };
        }
        
        if (filteredComparables.length === 0) return { avgRent: 0, avgPsf: 0, totalUnits: 0, count: 0 };
        
        let prices = [];
        let psfValues = [];
        let unitCount = 0;

        filteredComparables.forEach(p => {
            if (p.units?.length > 0) {
                p.units.forEach(u => {
                    const price = parseFloat(String(u.price || '0').replace(/[$,–-]/g, '').split(' ')[0]) || 0;
                    const sqft = parseInt(String(u.sqft || '0').replace(/[,\s]/g, '').match(/\d+/)?.[0]) || 0;
                    if (price > 0) {
                        prices.push(price);
                        unitCount++;
                    }
                    if (price > 0 && sqft > 0) psfValues.push(price / sqft);
                });
            }
        });

        return {
            avgRent: prices.length ? Math.round(prices.reduce((a, b) => a + b, 0) / prices.length) : 0,
            avgPsf: psfValues.length ? (psfValues.reduce((a, b) => a + b, 0) / psfValues.length).toFixed(2) : 0,
            totalUnits: unitCount,
            count: filteredComparables.length
        };
    };

    const calculateRecommendedPrice = () => {
        // Use analysis data if available, otherwise use computed stats
        const basePrice = analysis?.market_clearing_price || getMarketStats().avgRent;
        if (basePrice === 0) return 0;
        
        let price = basePrice;
        
        // Floor adjustment
        if (floorLevel > 1) price += price * 0.02 * (floorLevel - 1);
        
        // Market adjustment
        price += price * (marketAdjustment / 100);
        
        // Amenities
        if (amenities.parking) price += 150;
        if (amenities.storage) price += 75;
        if (amenities.petFriendly) price += 50;
        if (amenities.balcony) price += 100;
        
        return Math.round(price);
    };

    const exportToCSV = () => {
        if (filteredComparables.length === 0) return;
        
        const rows = [['Property', 'Address', 'Township', 'Unit', 'Beds', 'Baths', 'Sqft', 'Price']];
        filteredComparables.forEach(p => {
            if (p.units?.length > 0) {
                p.units.forEach(u => {
                    rows.push([
                        `"${p.name || p.property_name || ''}"`,
                        `"${p.address || ''}"`,
                        `"${p.township || ''}"`,
                        `"${u.model || ''}"`,
                        u.beds || '',
                        u.baths || '',
                        u.sqft || '',
                        u.price || ''
                    ]);
                });
            }
        });
        
        const csv = rows.map(r => r.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `comparables_${targetLocation || 'all'}_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
    };

    // ==================== EFFECTS ====================

    useEffect(() => {
        fetchProperties();
        fetchComparablesFromDB();
        fetchModelInfo();
    }, []);

    useEffect(() => {
        if (propertyIdFromUrl && properties.length > 0) {
            const prop = properties.find(p => p.id === parseInt(propertyIdFromUrl));
            if (prop) {
                setSelectedProperty(prop);
                const city = extractCity(prop.address);
                const state = extractState(prop.address);
                if (city) {
                    setTargetLocation(`${city}${state ? ', ' + state : ''}`);
                }
            }
        }
    }, [propertyIdFromUrl, properties]);

    useEffect(() => {
        if (unitTypeIdFromUrl && selectedProperty?.unit_types) {
            const ut = selectedProperty.unit_types.find(u => u.id === parseInt(unitTypeIdFromUrl));
            if (ut) setSelectedUnitType(ut);
        }
    }, [unitTypeIdFromUrl, selectedProperty]);

    const stats = getMarketStats();
    const recommendedPrice = calculateRecommendedPrice();

    // ==================== RENDER ====================

    return (
        <div className="pricing-engine">
            <Navbar selected="Pricing Engine" />
            
            <div className="pe-container">
                {/* Header */}
                <header className="pe-header">
                    <div className="pe-header-content">
                        <h1>🏠 Pricing Engine</h1>
                        <p>AI-powered rental pricing using market comparables and hedonic modeling</p>
                    </div>
                    {propertyIdFromUrl && (
                        <button className="pe-back-btn" onClick={() => navigate(-1)}>
                            ← Back to Property
                        </button>
                    )}
                </header>

                {/* Main Grid */}
                <div className="pe-grid">
                    
                    {/* LEFT COLUMN - Input & Controls */}
                    <div className="pe-column pe-column-left">
                        
                        {/* Property & Unit Type Selection */}
                        <section className="pe-card">
                            <div className="pe-card-header">
                                <span className="pe-step">1</span>
                                <h2>Target Property & Unit Type</h2>
                            </div>
                            <div className="pe-card-body">
                                <label className="pe-label">Property</label>
                                <select 
                                    value={selectedProperty?.id || ''} 
                                    onChange={(e) => {
                                        const prop = properties.find(p => p.id === parseInt(e.target.value));
                                        setSelectedProperty(prop);
                                        setSelectedUnitType(null); // Reset unit type when property changes
                                        if (prop?.address) {
                                            const city = extractCity(prop.address);
                                            const state = extractState(prop.address);
                                            setTargetLocation(`${city}${state ? ', ' + state : ''}`);
                                        }
                                    }}
                                    className="pe-select"
                                >
                                    <option value="">Select a property...</option>
                                    {properties.map(p => (
                                        <option key={p.id} value={p.id}>{p.name} - {p.address}</option>
                                    ))}
                                </select>
                                
                                {/* Unit Type Selection - shows when property is selected */}
                                {selectedProperty && selectedProperty.unit_types && selectedProperty.unit_types.length > 0 && (
                                    <>
                                        <label className="pe-label" style={{marginTop: '1rem'}}>Unit Type</label>
                                        <select 
                                            value={selectedUnitType?.id || ''} 
                                            onChange={(e) => {
                                                const ut = selectedProperty.unit_types.find(u => u.id === parseInt(e.target.value));
                                                setSelectedUnitType(ut);
                                            }}
                                            className="pe-select"
                                        >
                                            <option value="">Select unit type to price...</option>
                                            {selectedProperty.unit_types.map(ut => (
                                                <option key={ut.id} value={ut.id}>
                                                    {ut.type_name || ut.name} - {ut.bedroom_count || ut.bedrooms}BR/{ut.bathroom_count || ut.bathrooms}BA 
                                                    {ut.square_footage ? ` - ${ut.square_footage} sqft` : ''}
                                                    {ut.minimum_expected_rent ? ` ($${ut.minimum_expected_rent}/mo)` : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </>
                                )}
                                
                                {/* Selected Property/Unit Info */}
                                {selectedProperty && (
                                    <div className="pe-selected-property">
                                        <strong>{selectedProperty.name}</strong>
                                        <span>{selectedProperty.address}</span>
                                        {selectedUnitType && (
                                            <div className="pe-unit-type-info">
                                                <span className="pe-unit-badge">
                                                    {selectedUnitType.type_name || selectedUnitType.name}
                                                </span>
                                                <span>{selectedUnitType.bedroom_count || selectedUnitType.bedrooms} BR</span>
                                                <span>{selectedUnitType.bathroom_count || selectedUnitType.bathrooms} BA</span>
                                                {selectedUnitType.square_footage && <span>{selectedUnitType.square_footage} sqft</span>}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </section>

                        {/* Location & Analysis */}
                        <section className="pe-card pe-card-primary">
                            <div className="pe-card-header">
                                <span className="pe-step">2</span>
                                <h2>Market Analysis</h2>
                            </div>
                            <div className="pe-card-body">
                                <label className="pe-label">Target Location (City, State)</label>
                                <input 
                                    type="text"
                                    value={targetLocation}
                                    onChange={(e) => setTargetLocation(e.target.value)}
                                    placeholder="e.g., Newark, NJ"
                                    className="pe-input"
                                    list="location-suggestions"
                                />
                                <datalist id="location-suggestions">
                                    {availableLocations.map(loc => (
                                        <option key={loc} value={loc} />
                                    ))}
                                </datalist>
                                
                                <p className="pe-hint">
                                    {availableLocations.includes(targetLocation) || availableLocations.some(l => l.toLowerCase().includes(targetLocation.toLowerCase()))
                                        ? '✓ Data exists in database'
                                        : '○ Will scrape new data'}
                                </p>

                                <div className="pe-scrape-settings">
                                    <label className="pe-label">Max new listings to scrape</label>
                                    <input 
                                        type="number"
                                        value={maxListings}
                                        onChange={(e) => setMaxListings(parseInt(e.target.value) || 15)}
                                        min={5}
                                        max={50}
                                        className="pe-input pe-input-small"
                                    />
                                </div>

                                <button 
                                    className="pe-btn pe-btn-primary"
                                    onClick={analyzeMarket}
                                    disabled={loading || !targetLocation.trim()}
                                >
                                    {loading ? '⏳ Processing...' : '🔍 Analyze Market'}
                                </button>

                                {filteredComparables.length > 0 && (
                                    <button 
                                        className="pe-btn pe-btn-secondary"
                                        onClick={scrapeMore}
                                        disabled={loading}
                                    >
                                        ➕ Scrape More Data
                                    </button>
                                )}

                                {statusMessage && (
                                    <div className={`pe-status ${statusMessage.includes('✅') ? 'pe-status-success' : ''}`}>
                                        {statusMessage}
                                    </div>
                                )}
                            </div>
                        </section>

                        {/* Database Stats */}
                        <section className="pe-card">
                            <div className="pe-card-header">
                                <h2>📊 Database</h2>
                            </div>
                            <div className="pe-card-body">
                                <div className="pe-db-stats">
                                    <div className="pe-db-stat">
                                        <span className="pe-db-value">{allComparables.length}</span>
                                        <span className="pe-db-label">Total Properties</span>
                                    </div>
                                    <div className="pe-db-stat">
                                        <span className="pe-db-value">{availableLocations.length}</span>
                                        <span className="pe-db-label">Locations</span>
                                    </div>
                                </div>
                                
                                {targetLocation && (
                                    <div className="pe-location-info">
                                        <strong>{targetLocation}:</strong>
                                        <span>{filteredComparables.length} properties</span>
                                    </div>
                                )}
                            </div>
                        </section>

                        {/* AI Model */}
                        <section className="pe-card">
                            <div className="pe-card-header">
                                <span className="pe-step">3</span>
                                <h2>Hedonic AI Model</h2>
                            </div>
                            <div className="pe-card-body">
                                <div className="pe-model-status">
                                    {modelInfo?.model_available ? (
                                        <>
                                            <span className="pe-status-dot pe-status-green"></span>
                                            <span>Model Ready</span>
                                            {modelInfo.latest_version && (
                                                <span className="pe-model-metric">
                                                    R² = {(modelInfo.latest_version.test_r2 * 100).toFixed(1)}%
                                                </span>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <span className="pe-status-dot pe-status-yellow"></span>
                                            <span>No trained model</span>
                                        </>
                                    )}
                                </div>

                                <label className="pe-checkbox-label">
                                    <input 
                                        type="checkbox"
                                        checked={useHedonicModel}
                                        onChange={(e) => setUseHedonicModel(e.target.checked)}
                                        disabled={!modelInfo?.model_available}
                                    />
                                    Use AI model for predictions
                                </label>

                                <button 
                                    className="pe-btn pe-btn-secondary"
                                    onClick={trainModel}
                                    disabled={trainingModel || allComparables.length < 10}
                                >
                                    {trainingModel ? '⏳ Training...' : '🧠 Train Model'}
                                </button>
                                
                                {allComparables.length < 10 && (
                                    <p className="pe-hint-small pe-warning">Need at least 10 properties to train.</p>
                                )}

                                {trainingResults && (
                                    <div className="pe-training-results">
                                        <strong>Training Complete!</strong>
                                        <span>R² = {(trainingResults.test_metrics?.r2 * 100).toFixed(1)}%</span>
                                        <span>MAE = ${trainingResults.test_metrics?.mae?.toFixed(0)}</span>
                                    </div>
                                )}
                            </div>
                        </section>
                    </div>

                    {/* RIGHT COLUMN - Results */}
                    <div className="pe-column pe-column-right">
                        
                        {/* Price Recommendation */}
                        <section className="pe-card pe-card-highlight">
                            <div className="pe-card-header">
                                <h2>💰 Recommended Price</h2>
                            </div>
                            <div className="pe-card-body">
                                <div className="pe-price-display">
                                    <span className="pe-price-amount">${recommendedPrice.toLocaleString()}</span>
                                    <span className="pe-price-period">/month</span>
                                </div>

                                {analysis?.hedonic_price && (
                                    <div className="pe-ai-price">
                                        <span>AI Prediction:</span>
                                        <strong>${Math.round(analysis.hedonic_price.median || analysis.hedonic_price).toLocaleString()}</strong>
                                    </div>
                                )}

                                <div className="pe-price-adjustments">
                                    <h4>Adjustments</h4>
                                    
                                    <label className="pe-label">Floor Level: {floorLevel}</label>
                                    <input 
                                        type="range"
                                        min={1}
                                        max={30}
                                        value={floorLevel}
                                        onChange={(e) => setFloorLevel(parseInt(e.target.value))}
                                        className="pe-slider"
                                    />

                                    <label className="pe-label">Market Adjustment: {marketAdjustment}%</label>
                                    <input 
                                        type="range"
                                        min={-20}
                                        max={20}
                                        value={marketAdjustment}
                                        onChange={(e) => setMarketAdjustment(parseInt(e.target.value))}
                                        className="pe-slider"
                                    />

                                    <div className="pe-amenities">
                                        {Object.entries(amenities).map(([key, value]) => (
                                            <label key={key} className="pe-amenity-btn">
                                                <input 
                                                    type="checkbox"
                                                    checked={value}
                                                    onChange={() => setAmenities(prev => ({...prev, [key]: !prev[key]}))}
                                                />
                                                <span>{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Market Stats */}
                        <section className="pe-card">
                            <div className="pe-card-header">
                                <h2>📈 Market Statistics</h2>
                                {targetLocation && <span className="pe-location-badge">{targetLocation}</span>}
                            </div>
                            {selectedUnitType && (
                                <p className="pe-unit-type-note">
                                    Based on comparables matching {selectedUnitType.bedroom_count || selectedUnitType.bedrooms}BR
                                    {selectedUnitType.bathroom_count || selectedUnitType.bathrooms ? ` / ${selectedUnitType.bathroom_count || selectedUnitType.bathrooms}BA` : ''}
                                </p>
                            )}
                            <div className="pe-card-body">
                                <div className="pe-stats-grid">
                                    <div className="pe-stat">
                                        <span className="pe-stat-value">${stats.avgRent.toLocaleString()}</span>
                                        <span className="pe-stat-label">Avg Rent</span>
                                    </div>
                                    <div className="pe-stat">
                                        <span className="pe-stat-value">${stats.avgPsf}</span>
                                        <span className="pe-stat-label">Avg $/sqft</span>
                                    </div>
                                    <div className="pe-stat">
                                        <span className="pe-stat-value">{stats.count}</span>
                                        <span className="pe-stat-label">Properties</span>
                                    </div>
                                    <div className="pe-stat">
                                        <span className="pe-stat-value">{stats.totalUnits}</span>
                                        <span className="pe-stat-label">Units</span>
                                    </div>
                                </div>

                                {analysis?.market_insights && (
                                    <div className="pe-insights">
                                        <div className="pe-insight">
                                            <span>Demand:</span>
                                            <span className={`pe-badge pe-badge-${analysis.market_insights.demand_level?.toLowerCase()}`}>
                                                {analysis.market_insights.demand_level}
                                            </span>
                                        </div>
                                        <div className="pe-insight">
                                            <span>Competition:</span>
                                            <span className={`pe-badge pe-badge-${analysis.market_insights.competition_level?.toLowerCase()}`}>
                                                {analysis.market_insights.competition_level}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </section>

                        {/* Comparables Table */}
                        <section className="pe-card pe-card-table">
                            <div className="pe-card-header">
                                <h2>🏘️ Comparable Properties</h2>
                                <button 
                                    className="pe-btn-icon"
                                    onClick={exportToCSV}
                                    disabled={filteredComparables.length === 0}
                                    title="Export to CSV"
                                >
                                    📥
                                </button>
                            </div>
                            <div className="pe-card-body pe-card-body-table">
                                {filteredComparables.length === 0 ? (
                                    <div className="pe-empty">
                                        <p>No comparable properties found.</p>
                                        <p className="pe-hint">
                                            {selectedUnitType 
                                                ? `No ${selectedUnitType.bedroom_count || selectedUnitType.bedrooms}BR comparables in this location. Try "Scrape More Data" or run analysis without selecting a unit type.`
                                                : 'Enter a location and click "Analyze Market"'}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="pe-table-container">
                                        <table className="pe-table">
                                            <thead>
                                                <tr>
                                                    <th>Property</th>
                                                    <th>Unit</th>
                                                    <th>Beds</th>
                                                    <th>Sqft</th>
                                                    <th>Price</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredComparables.slice(0, 30).flatMap((prop, i) => 
                                                    (prop.units?.length > 0 ? prop.units.slice(0, 3) : [{}]).map((unit, j) => (
                                                        <tr key={`${i}-${j}`}>
                                                            {j === 0 && (
                                                                <td rowSpan={Math.min(prop.units?.length || 1, 3)} className="pe-td-property">
                                                                    <strong>{prop.name || prop.property_name}</strong>
                                                                    <span>{prop.township || extractCity(prop.address)}</span>
                                                                </td>
                                                            )}
                                                            <td>{unit.model || '-'}</td>
                                                            <td>{unit.beds || '-'}</td>
                                                            <td>{(() => {
                                                                // Smart detection: sqft should be numeric, not contain $
                                                                const sqftVal = unit.sqft;
                                                                const priceVal = unit.price;
                                                                const bathsVal = unit.baths;
                                                                
                                                                // If sqft contains $, it's actually price (data was swapped)
                                                                if (sqftVal && sqftVal.toString().includes('$')) {
                                                                    // Check if baths looks like sqft (numeric, 3+ digits)
                                                                    if (bathsVal && /^\d{3,}$/.test(bathsVal.toString().replace(/,/g, ''))) {
                                                                        return bathsVal;
                                                                    }
                                                                    return '-';
                                                                }
                                                                return sqftVal || '-';
                                                            })()}</td>
                                                            <td className="pe-td-price">{(() => {
                                                                // Smart detection: price should contain $ or be numeric
                                                                const sqftVal = unit.sqft;
                                                                const priceVal = unit.price;
                                                                
                                                                // If price exists and has value, use it
                                                                if (priceVal && priceVal !== 'N/A' && priceVal !== 'None') {
                                                                    return priceVal;
                                                                }
                                                                // If sqft contains $, it's actually the price
                                                                if (sqftVal && sqftVal.toString().includes('$')) {
                                                                    return sqftVal;
                                                                }
                                                                return '-';
                                                            })()}</td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                        {filteredComparables.length > 30 && (
                                            <p className="pe-table-note">Showing 30 of {filteredComparables.length} properties</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </section>
                    </div>
                </div>

                {/* Error Display */}
                {error && (
                    <div className="pe-error">
                        ⚠️ {error}
                        <button onClick={() => setError(null)}>×</button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PricingEngine;
